// src/components/EmailAccessPage.jsx
// MSAL email access flow via Firebase Functions

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getStorage, ref, listAll } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getEmailAccessErrorMessage } from '../utils/emailAccessErrors';
import '../styles/EmailAccessPage.css';

export default function EmailAccessPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('ready'); // ready, connecting, waiting, checking, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const popupRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Single-click handler - does everything
  const handleConnect = async () => {
    if (!currentUser?.uid) {
      setErrorMessage('Please sign in first');
      setStep('error');
      return;
    }

    setStep('connecting');
    setErrorMessage('');

    try {
      // Get auth URL from Firebase Function
      const functions = getFunctions();
      const initiateMsalAuth = httpsCallable(functions, 'initiateMsalAuth');
      const result = await initiateMsalAuth({ uid: currentUser.uid });

      if (!result.data?.authUrl) {
        throw new Error('Failed to get authentication URL');
      }

      // Open popup
      setStep('waiting');
      popupRef.current = window.open(
        result.data.authUrl,
        'msalAuth',
        'width=500,height=700,left=200,top=100'
      );

      // Start polling for popup close AND token existence
      pollIntervalRef.current = setInterval(async () => {
        // Check if popup closed
        if (popupRef.current?.closed) {
          clearInterval(pollIntervalRef.current);
          setStep('checking');
          await checkForToken();
        }
      }, 500);

    } catch (err) {
      console.error('MSAL init error:', err);
      setErrorMessage(getEmailAccessErrorMessage(err));
      setStep('error');
    }
  };

  // Check if token exists in Firebase Storage
  const checkForToken = async () => {
    try {
      const storage = getStorage();
      const pathRef = ref(storage, `msal_caches/${currentUser.uid}`);
      const result = await listAll(pathRef);

      if (result.items.length > 0) {
        // Token found! Update Firestore and redirect
        await updateDoc(doc(db, 'users', currentUser.uid), {
          hasMsalToken: true,
          msalLinkedAt: serverTimestamp(),
        });

        setStep('success');

        // Redirect after brief celebration
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        // No token found
        setErrorMessage('Setup was not completed. Please try again.');
        setStep('error');
      }
    } catch (err) {
      console.error('Token check error:', err);
      setErrorMessage(getEmailAccessErrorMessage(err));
      setStep('error');
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    setStep('ready');
    setErrorMessage('');
  };

  return (
    <div className="landing-background">
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="landing-container">
        <div className="hero-section">
          <h1 className="hero-title">Connect Your Email</h1>
          <p className="hero-subtitle">
            Link your Microsoft account to enable email automation. This takes about 30 seconds.
          </p>
        </div>

        {/* Error state */}
        {step === 'error' && errorMessage && (
          <div className="error-card">
            <span className="error-icon">❌</span>
            <p>{errorMessage}</p>
          </div>
        )}

        <div className="login-card">
          {/* Ready state */}
          {step === 'ready' && (
            <>
              <h2>🔑 Email Access Setup</h2>
              <p>Click below to securely connect your Microsoft email account.</p>
              <button className="login-button" onClick={handleConnect}>
                <svg width="20" height="20" viewBox="0 0 32 32" fill="none" style={{ marginRight: '0.5rem' }}>
                  <rect x="17" y="17" width="10" height="10" fill="#FEBA08"/>
                  <rect x="5" y="17" width="10" height="10" fill="#05A6F0"/>
                  <rect x="17" y="5" width="10" height="10" fill="#80BC06"/>
                  <rect x="5" y="5" width="10" height="10" fill="#F25325"/>
                </svg>
                Connect with Microsoft
              </button>
            </>
          )}

          {/* Connecting state */}
          {step === 'connecting' && (
            <>
              <h2>Connecting...</h2>
              <p>Preparing secure connection to Microsoft</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
                <div className="spinner" />
              </div>
              <button
                className="login-button"
                onClick={handleCancel}
                style={{ backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                ← Cancel
              </button>
            </>
          )}

          {/* Waiting for popup */}
          {step === 'waiting' && (
            <>
              <h2>Sign in with Microsoft</h2>
              <p>Complete the sign-in in the popup window. This page will update automatically when you're done.</p>

              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                margin: '1rem 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem'
              }}>
                <div className="spinner" />
                <span>Waiting for Microsoft sign-in...</span>
              </div>

              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '12px',
                marginBottom: '1rem',
                textAlign: 'left',
              }}>
                <p style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#fbbf24' }}>
                  Don't see a popup?
                </p>
                <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Your browser may have blocked it. Look for a popup icon in your address bar and click "Allow popups" for this site.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  className="login-button"
                  onClick={handleCancel}
                  style={{ flex: 1, backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  ← Back
                </button>
                <button
                  className="login-button"
                  onClick={handleConnect}
                  style={{ flex: 1 }}
                >
                  Retry
                </button>
              </div>
            </>
          )}

          {/* Checking token */}
          {step === 'checking' && (
            <>
              <h2>Verifying...</h2>
              <p>Confirming your email access was set up correctly</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
                <div className="spinner" />
              </div>
              <button
                className="login-button"
                onClick={handleCancel}
                style={{ backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                ← Cancel
              </button>
            </>
          )}

          {/* Success */}
          {step === 'success' && (
            <>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 0 60px rgba(34, 197, 94, 0.4)',
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2>You're Connected!</h2>
              <p>Redirecting to your dashboard...</p>
            </>
          )}

          {/* Error state - show retry button */}
          {step === 'error' && (
            <>
              <h2>Connection Failed</h2>
              <button className="login-button" onClick={handleConnect}>
                <svg width="20" height="20" viewBox="0 0 32 32" fill="none" style={{ marginRight: '0.5rem' }}>
                  <rect x="17" y="17" width="10" height="10" fill="#FEBA08"/>
                  <rect x="5" y="17" width="10" height="10" fill="#05A6F0"/>
                  <rect x="17" y="5" width="10" height="10" fill="#80BC06"/>
                  <rect x="5" y="5" width="10" height="10" fill="#F25325"/>
                </svg>
                Try Again
              </button>
            </>
          )}

          <div className="security-note">
            <span className="security-icon">🛡️</span>
            <span>Your email credentials are never stored. Only secure tokens are used.</span>
          </div>
        </div>

        {/* User info */}
        {currentUser && (
          <p style={{ marginTop: '2rem', opacity: 0.5, fontSize: '0.875rem' }}>
            Signed in as {currentUser.email}
          </p>
        )}
      </div>
    </div>
  );
}
